import {
    ApolloClient,
    ApolloLink,
    InMemoryCache,
    HttpLink,
} from '@apollo/client';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { ErrorLink } from '@apollo/client/link/error';
import { SetContextLink } from '@apollo/client/link/context';
import { env } from '@/lib/env';
import { getAccessToken } from '@/lib/auth/tokenStore';
import { notifySessionExpired } from '@/lib/auth/sessionEvents';
import {
    isNotAuthorisedError,
    PERMISSIONS_OPERATION_NAME,
} from '@/lib/apollo/authErrors';

/**
 * The Apollo Client for the GraphQL half of the Dawn Patrol API
 * (`POST /graphql`). The Express half — auth, OAuth, `/me`, `/spots`,
 * `/subscription` — goes through `@/lib/api/httpClient` instead. Same origin,
 * different path.
 *
 * Built by a factory (not a module-level singleton) so tests and Storybook can
 * create isolated clients with their own link/cache and no shared state.
 */
export function createApolloClient(
    options: { uri?: string; link?: HttpLink } = {}
): ApolloClient {
    const httpLink =
        options.link ??
        new HttpLink({
            uri: options.uri ?? env.graphqlEndpoint,
        });

    // The API authenticates with `Authorization: Bearer <token>` — there is no
    // cookie session, so the header has to be attached per request. Read from
    // the store at call time, never captured at client construction, or the
    // client would keep sending whatever token existed at startup.
    const authLink = new SetContextLink((prevContext) => {
        const token = getAccessToken();
        if (!token) {
            return prevContext;
        }

        return {
            ...prevContext,
            headers: {
                ...prevContext.headers,
                Authorization: `Bearer ${token}`,
            },
        };
    });

    const errorLink = new ErrorLink(({ error, operation }) => {
        // Transport/network failures and GraphQL errors both land here. This is
        // observability only — it shows the user nothing. Each hook's consumer
        // renders its own error state.
        // Messages only, never the error object: a `CombinedGraphQLErrors`
        // carries the response `data`, which can hold a one-time secret.
        console.error(
            `[graphql] operation "${operation.operationName}" failed:`,
            CombinedGraphQLErrors.is(error)
                ? error.errors.map((e) => e.message)
                : error instanceof Error
                  ? error.message
                  : 'unknown error'
        );

        if (!isNotAuthorisedError(error)) {
            return;
        }

        // The API cannot tell "expired" from "forbidden" on the wire. A
        // failed permissions query only needs a session, so it can only mean
        // the session expired. Any other operation failing re-runs the
        // permissions query: it then either expires the session, or tells the
        // gate the user is not an admin. Both are decoupled from link
        // processing with a microtask, and a permissions failure never
        // refetches, so there is no loop.
        if (operation.operationName === PERMISSIONS_OPERATION_NAME) {
            queueMicrotask(notifySessionExpired);
            return;
        }

        const { client } = operation;
        queueMicrotask(() => {
            void Promise.resolve(
                client.refetchQueries({ include: [PERMISSIONS_OPERATION_NAME] })
            ).catch(() => undefined);
        });
    });

    return new ApolloClient({
        link: ApolloLink.from([errorLink, authLink, httpLink]),
        cache: new InMemoryCache({
            typePolicies: {
                // `adminUser` mixes a per-user `state` into every provider, so
                // one shared `SocialAccountProvider:<id>` record would let
                // viewing user B overwrite user A's state (RF7).
                SocialAccountProvider: { keyFields: false },
                // Different queries select different subsets of these id-less
                // children; merge them rather than replace.
                User: {
                    fields: {
                        subscription: { merge: true },
                        activation: { merge: true },
                    },
                },
                // `adminUpdateSpot` re-fetches `AdminSpot`; its id-less children
                // merge rather than replace, as for User above. The list
                // selects a subset of `beach_orientation` and the pane all of
                // it; `merge: true` keeps both (an incoming null still wins).
                Spot: {
                    fields: {
                        location: { merge: true },
                        configuration: { merge: true },
                        beach_orientation: { merge: true },
                    },
                },
                // The list selects a subset of `manual` and `approval`, the pane
                // all of them: merge so one never drops the other's fields. A
                // clear or a revoke sends null, which still wins.
                BeachOrientation: {
                    fields: {
                        manual: { merge: true },
                        approval: { merge: true },
                    },
                },
                // Periods have no `id`; key them so latest_period, periods[i]
                // and the leaderboard's period are one record and documents
                // may select subsets of its fields.
                AdminChallengePeriod: { keyFields: ['challenge_instance_id'] },
                Query: {
                    fields: {
                        // Pages of one user's sessions share one list: the
                        // list is keyed by user alone, and each page is
                        // written at its `skip`. A refetch from 0 replaces
                        // the list.
                        adminSessions: {
                            keyArgs: ['user_id'],
                            merge(
                                existing: readonly unknown[] | undefined,
                                incoming: readonly unknown[] | null,
                                { args }
                            ) {
                                if (incoming === null) return null;
                                const merged = existing
                                    ? existing.slice(0)
                                    : [];
                                const offset =
                                    typeof args?.skip === 'number'
                                        ? args.skip
                                        : 0;
                                for (let i = 0; i < incoming.length; i += 1) {
                                    merged[offset + i] = incoming[i];
                                }
                                // From the start, the page is the list: drop
                                // a stale tail from before the refetch.
                                if (offset === 0) {
                                    merged.length = incoming.length;
                                }
                                return merged;
                            },
                        },
                    },
                },
                // The schema's per-user `type Subscription` collides with
                // Apollo's default root typename "Subscription". This app has
                // no GraphQL subscriptions, so move the root name off it and
                // `User.subscription` is an ordinary object.
                NoSubscriptionRoot: { subscriptionType: true },
            },
        }),
        devtools: { enabled: env.isDevelopment },
    });
}
